import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { LIFECYCLE_FIELD_DESCRIPTORS } from './_lifecycleFields.js';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { TICKET_WORKSPACE, TICKET_WORKFLOW_STAGE, TICKET_AUTOMATION_RULE, TICKET_AUTOMATION_EXECUTION, } from '../types/ticket-workspace.js';
import { FIELD_LIMITS, MAX_AUTOMATION_TIMEOUT_MS, MAX_STAGE_ORDER_INDEX, } from '../validation/fieldLimits.js';
export const ticketWorkspaceDescriptor = {
    name: TICKET_WORKSPACE,
    label: 'Espaço de tickets',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticketWorkspace',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Configurable ticket workspace within an organization',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'organizationId', label: 'Organização', type: 'string', required: true },
        { name: 'name', label: 'Nome', type: 'string', required: true, maxLength: FIELD_LIMITS.NAME },
        {
            name: 'key',
            label: 'Chave',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.KEY,
            // Unique per org (validateTicketWorkspace). Not `unique: true` — that flag means
            // a numeric code to OrgScopedCrudService. `copy: false` leaves it blank on a
            // record copy (spec 0051 D17) instead of carrying a key the save would reject.
            copy: false,
            description: 'URL-safe unique identifier within the organization',
        },
        {
            name: 'description',
            label: 'Descrição',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.DESCRIPTION,
        },
        { name: 'sectorId', label: 'Setor', type: 'string', required: false },
        { name: 'isPortalVisible', label: 'Visível no portal', type: 'boolean', required: true },
        { name: 'isAdminVisible', label: 'Visível no admin', type: 'boolean', required: true },
        {
            name: 'memberUserIds',
            label: 'Equipe',
            type: 'array',
            required: false,
            description: 'User IDs who work tickets on this board. Empty or absent = everyone in the organization. Does not affect the portal.',
        },
        {
            name: 'defaultTicketType',
            label: 'Tipo padrão',
            type: 'enum',
            required: false,
            enumValues: ['BUG', 'IMPROVEMENT', 'REQUEST', 'QUESTION'],
        },
        {
            name: 'defaultPriority',
            label: 'Prioridade padrão',
            type: 'enum',
            required: false,
            enumValues: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'],
        },
        {
            name: 'ticketCounter',
            label: 'Contador de tickets',
            type: 'number',
            required: false,
            description: 'Managed by system-api; do not edit',
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'sectorId',
            targetEntity: 'Sector',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Linked productive sector',
        },
        {
            fieldName: 'id',
            targetEntity: 'TicketWorkflowStage',
            relationship: 'one-to-many',
            description: 'Workflow stages in this workspace',
        },
        {
            fieldName: 'id',
            targetEntity: 'TicketAutomationRule',
            relationship: 'one-to-many',
            description: 'Automation rules in this workspace',
        },
        {
            fieldName: 'id',
            targetEntity: 'WorkspaceTicket',
            relationship: 'one-to-many',
            description: 'Tickets in this workspace',
        },
    ],
};
export const ticketWorkflowStageDescriptor = {
    name: TICKET_WORKFLOW_STAGE,
    label: 'Estágio do fluxo',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticketWorkspace/{workspaceId}/stage',
    pathPatternKeys: {
        orgId: 'string',
        workspaceId: 'string',
    },
    version: 1,
    description: 'One configurable stage in a workspace workflow',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'workspaceId', label: 'Espaço', type: 'string', required: true },
        { name: 'organizationId', label: 'Organização', type: 'string', required: true },
        { name: 'name', label: 'Nome', type: 'string', required: true, maxLength: FIELD_LIMITS.NAME },
        {
            name: 'key',
            label: 'Chave',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.KEY,
            description: 'URL-safe unique identifier within the workspace',
        },
        {
            name: 'description',
            label: 'Descrição',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.DESCRIPTION,
        },
        {
            name: 'orderIndex',
            label: 'Ordem',
            type: 'number',
            required: true,
            description: `Display order on the board (ascending). Range: 0–${MAX_STAGE_ORDER_INDEX}.`,
        },
        {
            name: 'color',
            label: 'Cor',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.COLOR,
        },
        // uiMetadata: Record<string, unknown> — omitted; complex object validated at service layer
        { name: 'isInitialStage', label: 'Estágio inicial', type: 'boolean', required: true },
        { name: 'isFinalStage', label: 'Estágio final', type: 'boolean', required: true },
        {
            name: 'defaultAssigneeUserId',
            label: 'Responsável padrão',
            type: 'string',
            required: false,
        },
        // defaultParticipantUserIds: string[] — declared as array for list validation
        {
            name: 'defaultParticipantUserIds',
            label: 'Participantes padrão',
            type: 'array',
            required: false,
            description: 'User IDs added as participants when a ticket enters this stage',
        },
        {
            name: 'notifyOnEnter',
            label: 'Notificar ao entrar',
            type: 'boolean',
            required: false,
            description: 'When true, notify recipients as a ticket enters this stage. Empty notifyUserIds → workspace team.',
        },
        {
            name: 'notifyUserIds',
            label: 'Quem recebe ao entrar',
            type: 'array',
            required: false,
            description: 'User IDs notified when a ticket enters this stage. Empty or absent with notifyOnEnter → workspace team.',
        },
        // permissions: TicketStagePermissions — omitted; complex object validated at service layer
        {
            name: 'allowedTransitionStageIds',
            label: 'Transições permitidas',
            type: 'array',
            required: false,
            description: 'Stage IDs this stage can transition to',
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'workspaceId',
            targetEntity: 'TicketWorkspace',
            relationship: 'many-to-one',
            description: 'Parent workspace',
        },
    ],
};
export const ticketAutomationRuleDescriptor = {
    name: TICKET_AUTOMATION_RULE,
    label: 'Regra de automação',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticketWorkspace/{workspaceId}/automationRule',
    pathPatternKeys: {
        orgId: 'string',
        workspaceId: 'string',
    },
    version: 1,
    description: 'Automation rule bound to a workspace or stage event',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'workspaceId', label: 'Espaço', type: 'string', required: true },
        { name: 'organizationId', label: 'Organização', type: 'string', required: true },
        {
            name: 'stageId',
            label: 'Estágio',
            type: 'string',
            required: false,
            description: 'If set, fires only for this stage; otherwise fires at workspace level',
        },
        { name: 'name', label: 'Nome', type: 'string', required: true, maxLength: FIELD_LIMITS.NAME },
        {
            name: 'description',
            label: 'Descrição',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.DESCRIPTION,
        },
        {
            name: 'triggerEvent',
            label: 'Evento disparador',
            type: 'enum',
            required: true,
            enumValues: [
                'ticket.created',
                'ticket.closed',
                'ticket.cancelled',
                'ticket.message.created',
                'ticket.stage.entered',
                'ticket.stage.exited',
                'ticket.assignee.changed',
                'ticket.approval.requested',
                'ticket.approval.completed',
                'ticket.attachment.added',
            ],
        },
        {
            name: 'executionMode',
            label: 'Modo de execução',
            type: 'enum',
            required: true,
            enumValues: ['sequential', 'parallel'],
        },
        // actions: TicketAutomationAction[] — omitted; complex array validated at service layer
        {
            name: 'scriptSource',
            label: 'Código do script',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.SCRIPT_SOURCE,
            description: 'Optional JS snippet (server-side sandbox only)',
        },
        {
            name: 'timeoutMs',
            label: 'Timeout (ms)',
            type: 'number',
            required: false,
            description: `Script timeout in ms (0–${MAX_AUTOMATION_TIMEOUT_MS}).`,
        },
        {
            name: 'integrationKey',
            label: 'Chave de integração',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'workspaceId',
            targetEntity: 'TicketWorkspace',
            relationship: 'many-to-one',
            description: 'Parent workspace',
        },
    ],
};
export const ticketAutomationExecutionDescriptor = {
    name: TICKET_AUTOMATION_EXECUTION,
    label: 'Execução de automação',
    domain: 'tickets',
    collectionPath: 'org/{orgId}/ticketAutomationExecution',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Immutable execution log for one automation rule run',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'automationRuleId', label: 'Regra de automação', type: 'string', required: true },
        { name: 'workspaceId', label: 'Espaço', type: 'string', required: true },
        { name: 'organizationId', label: 'Organização', type: 'string', required: true },
        { name: 'ticketId', label: 'Ticket', type: 'string', required: true },
        {
            name: 'triggerEvent',
            label: 'Evento disparador',
            type: 'enum',
            required: true,
            enumValues: [
                'ticket.created',
                'ticket.closed',
                'ticket.cancelled',
                'ticket.message.created',
                'ticket.stage.entered',
                'ticket.stage.exited',
                'ticket.assignee.changed',
                'ticket.approval.requested',
                'ticket.approval.completed',
                'ticket.attachment.added',
            ],
        },
        {
            name: 'status',
            label: 'Status',
            type: 'enum',
            required: true,
            enumValues: ['success', 'error', 'timeout', 'skipped'],
        },
        {
            name: 'errorMessage',
            label: 'Mensagem de erro',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.TICKET_DETAILS,
        },
        {
            name: 'durationMs',
            label: 'Duração (ms)',
            type: 'number',
            display: 'duration',
            required: false,
            description: `Execution duration in ms (0–${MAX_AUTOMATION_TIMEOUT_MS}).`,
        },
        { name: 'executedAt', label: 'Executado em', type: 'date', display: 'datetime', required: true },
        // context: TicketAutomationContext — omitted; complex object stored for debugging only
    ],
    foreignKeys: [
        {
            fieldName: 'automationRuleId',
            targetEntity: 'TicketAutomationRule',
            relationship: 'many-to-one',
            description: 'Automation rule that was executed',
        },
        {
            fieldName: 'ticketId',
            targetEntity: 'WorkspaceTicket',
            relationship: 'many-to-one',
            description: 'Ticket that triggered the automation',
        },
    ],
};
//# sourceMappingURL=ticket-workspace.descriptor.js.map